import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import SendIcon from "@mui/icons-material/Send";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

import useBulkEmailData from "layouts/tables/data/bulkEmailData";

function BulkEmail() {
  const {
    columns,
    rows,
    isLoading,
    error,
    members,
    composeOpen,
    setComposeOpen,
    subject,
    setSubject,
    message,
    setMessage,
    selectedMemberIds,
    setSelectedMemberIds,
    isSending,
    handleCompose,
    handleSelectAll,
    handleSend,
  } = useBulkEmailData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading email logs...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  const selectedMembers = members.filter((m) => selectedMemberIds.includes(m.id));

  return (
    <Box>
      <PageHeader
        title="Bulk Email"
        subtitle="Send newsletters, receipts, or announcements to members by email."
        actions={
          <Button variant="contained" startIcon={<SendIcon />} onClick={handleCompose}>
            Compose Email
          </Button>
        }
      />

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search email log..."
        />
      </Card>

      <FormDialog
        open={composeOpen}
        onClose={() => !isSending && setComposeOpen(false)}
        title="Compose Bulk Email"
        onSave={handleSend}
        saveLabel={isSending ? "Sending..." : "Send Email"}
        saveDisabled={
          isSending || !subject.trim() || !message.trim() || selectedMemberIds.length === 0
        }
        loading={isSending}
        maxWidth="md"
      >
        <FormSection>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={members}
            getOptionLabel={(m) => `${m.first_name} ${m.surname} (${m.email})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedMembers}
            onChange={(_, newValue) => setSelectedMemberIds(newValue.map((m) => m.id))}
            disabled={isSending}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={`${option.first_name} ${option.surname}`}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Recipients"
                placeholder="Search members with an email address..."
                margin="normal"
                helperText={`${selectedMemberIds.length} of ${members.length} members selected`}
              />
            )}
          />
          <Button size="small" onClick={handleSelectAll} disabled={isSending} sx={{ mt: 1 }}>
            {selectedMemberIds.length === members.length ? "Deselect All" : "Select All"}
          </Button>
        </FormSection>

        <FormSection>
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={isSending}
          />
          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            margin="normal"
            required
            multiline
            rows={8}
            disabled={isSending}
          />
        </FormSection>
      </FormDialog>
    </Box>
  );
}

export default BulkEmail;
